import { memo } from 'react';
import { Pressable, View } from 'react-native';

import { Text } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { LanguageOptionItemProps } from './types';

const RadioButton = memo<{ isSelected: boolean }>(({ isSelected }) => (
  <View
    style={[
      styles.radioButtonContainer,
      isSelected && styles.radioButtonContainerSelected,
    ]}
  >
    {isSelected && <View style={styles.radioButtonInner} />}
  </View>
));

RadioButton.displayName = 'RadioButton';

const LanguageOptionItem = memo<LanguageOptionItemProps>(
  ({
    value,
    nameKey,
    isSelected,
    disabled = false,
    onSelect,
    getLanguageNameInCurrentLanguage,
    testID,
  }) => {
    const { t } = useScreenTranslations('LanguageBottomSheet');

    const label = t(nameKey);
    const translatedName = getLanguageNameInCurrentLanguage(
      `LanguageBottomSheet.languageNames.${nameKey}`,
    );

    return (
      <Pressable
        onPress={() => onSelect(value)}
        disabled={disabled}
        style={[
          styles.languageOption,
          isSelected && styles.languageOptionSelected,
        ]}
        accessibilityRole="radio"
        aria-checked={isSelected}
        aria-disabled={disabled || undefined}
        accessibilityLabel={
          isSelected
            ? t('accessibility.radioButtonSelected', { language: label })
            : t('accessibility.radioButton', { language: label })
        }
        accessibilityHint={t('accessibility.selectLanguageHint', {
          language: label,
        })}
        testID={testID}
      >
        <View style={styles.textContainer}>
          <Text variant="l" color="textPrimary">
            {label}
          </Text>
          <Text variant="m" color="textTertiary">
            {translatedName}
          </Text>
        </View>
        <RadioButton isSelected={isSelected} />
      </Pressable>
    );
  },
);

LanguageOptionItem.displayName = 'LanguageOptionItem';
export { LanguageOptionItem };
